import { useCallback, useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { SiniestroItem, SiniestroStatus } from '../types/siniestros.types';
import { FiltersState } from '../components/SiniestrosFilters/types';
import {
    useExportSiniestrosMutation,
    useExportSrtFormularioArchivoMutation,
    useGetSiniestrosQuery
} from '../../../services/roamApi';
import { VisitaEstado } from '../../Visitas/consts/visitasStatus';
import { mapRoamSiniestro } from '../adapters/roam';
import { buildSiniestrosQueryParams, TAB_STATUS_MAP } from '../utils/siniestrosQueryParams';
import { descargarDesdeUrl } from '../utils/descargarDesdeUrl';
import { useDescargarExpedienteZipMutation } from '../../../services/visitasApi';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import useServerExcelExport from '../../../utils/hooks/useServerExcelExport';

interface UseSiniestrosResult {
    activeTab: SiniestroStatus;
    setActiveTab: (status: SiniestroStatus) => void;
    siniestros: SiniestroItem[];
    isLoading: boolean;
    totalCount: number;
    refetch: () => void;
    selectedIds: Set<number>;
    seleccionEstado: 'none' | 'some' | 'all';
    isSeleccionable: (siniestro: SiniestroItem) => boolean;
    handleToggleSeleccion: (id: number, siniestro: SiniestroItem) => void;
    handleSelectAll: () => void;
    handleClearSeleccion: () => void;
    handleGenerarTxtMasivo: () => Promise<void>;
    isGenerandoTxt: boolean;
    handleDescargarZip: (siniestro: SiniestroItem) => Promise<void>;
    isLoadingZip: boolean;
    zipDialogOpen: boolean;
    zipArchivosAusentes: string[];
    zipPresignedUrl: string | null;
    handleConfirmarDescargaZip: () => void;
    handleCancelarDescargaZip: () => void;
    handleExportarExcel: () => Promise<void>;
    isExportandoExcel: boolean;
}

interface UseSiniestrosParams {
    filters?: Record<string, any> | null;
    extraFilters?: FiltersState | null;
    isSupervisorSIMP?: boolean;
}

const isSeleccionable = (siniestro: SiniestroItem): boolean =>
    !!siniestro.idAccidenteLaboral &&
    (siniestro.visitasRoam?.some((v) => v.estadoId === VisitaEstado.PRESENTADO) ?? false);

const useSiniestros = ({
    filters,
    extraFilters,
    isSupervisorSIMP = false
}: UseSiniestrosParams = {}): UseSiniestrosResult => {
    const [activeTab, setActiveTab] = useState<SiniestroStatus>('ALL');
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [exportSrtFormularioArchivo, { isLoading: isGenerandoTxt }] =
        useExportSrtFormularioArchivoMutation();
    const [descargarExpedienteZip, { isLoading: isLoadingZip }] =
        useDescargarExpedienteZipMutation();
    const [exportSiniestros, { isLoading: isExportandoExcel }] = useExportSiniestrosMutation();
    const { exportToExcel } = useServerExcelExport(exportSiniestros);
    const [zipDialogOpen, setZipDialogOpen] = useState(false);
    const [zipArchivosAusentes, setZipArchivosAusentes] = useState<string[]>([]);
    const [zipPresignedUrl, setZipPresignedUrl] = useState<string | null>(null);

    // Empleado y Nro ROAM son texto libre: se consulta al dejar de tipear (SMP-365)
    const empleadoDebounced = useDebouncedValue(extraFilters?.employee, 400);
    const nroRoamDebounced = useDebouncedValue(extraFilters?.nroRoam || filters?.siniestro, 400);

    const queryParams = useMemo(() => {
        // Al supervisor el back le fija el alcance (visitas Presentadas de sus organismos, en
        // cualquier estado del siniestro): no se envían estado ni preventor (SMP-460)
        return buildSiniestrosQueryParams({
            filters,
            extraFilters,
            idsEstado: isSupervisorSIMP ? undefined : TAB_STATUS_MAP[activeTab],
            idPreventor: isSupervisorSIMP ? undefined : extraFilters?.idPreventor,
            empleado: empleadoDebounced,
            nroRoam: nroRoamDebounced
        });
    }, [activeTab, extraFilters, filters, empleadoDebounced, nroRoamDebounced, isSupervisorSIMP]);

    const { data, isLoading, isFetching, refetch } = useGetSiniestrosQuery(queryParams);

    const siniestros = useMemo(() => (data?.data || []).map(mapRoamSiniestro), [data]);

    useEffect(() => {
        setSelectedIds(new Set());
    }, [extraFilters?.clientId, filters?.skip]);

    const seleccionEstado = useMemo((): 'none' | 'some' | 'all' => {
        const habilitados = siniestros.filter(isSeleccionable);
        if (habilitados.length === 0 || selectedIds.size === 0) return 'none';
        const seleccionados = habilitados.filter((s) => selectedIds.has(Number(s.id)));
        if (seleccionados.length === habilitados.length) return 'all';
        if (seleccionados.length > 0) return 'some';
        return 'none';
    }, [siniestros, selectedIds]);

    const handleToggleSeleccion = useCallback(
        (id: number, siniestro: SiniestroItem) => {
            if (!extraFilters?.clientId) {
                enqueueSnackbar('Para exportar, seleccioná un cliente en el filtro primero.', {
                    variant: 'warning'
                });
                return;
            }
            if (!isSeleccionable(siniestro)) return;
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        },
        [extraFilters?.clientId]
    );

    const handleSelectAll = useCallback(() => {
        const habilitados = siniestros.filter(isSeleccionable).map((s) => Number(s.id));
        setSelectedIds(new Set(habilitados));
    }, [siniestros]);

    const handleClearSeleccion = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const handleGenerarTxtMasivo = useCallback(async () => {
        const idsAccidenteLaboral = siniestros
            .filter((s) => selectedIds.has(Number(s.id)) && s.idAccidenteLaboral)
            .map((s) => s.idAccidenteLaboral as number);

        if (idsAccidenteLaboral.length === 0) {
            enqueueSnackbar('No hay siniestros con formulario SRT listo para exportar.', {
                variant: 'warning'
            });
            return;
        }

        try {
            const response = await exportSrtFormularioArchivo({
                idsAccidenteLaboral
            }).unwrap();

            if (!response?.presignedUrl) throw new Error('Missing presignedUrl');

            const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const clienteNombre = (extraFilters?.clientId ?? 'cliente')
                .toString()
                .replace(/[^a-zA-Z0-9]/g, '_');

            await descargarDesdeUrl(
                response.presignedUrl,
                `SRT_export_${clienteNombre}_${fecha}.zip`
            );
            setSelectedIds(new Set());
        } catch (error: any) {
            enqueueSnackbar(
                error?.data?.message || 'Error al generar los archivos SRT.',
                { variant: 'error' }
            );
        }
    }, [siniestros, selectedIds, exportSrtFormularioArchivo, extraFilters?.clientId]);

    const handleDescargarZip = useCallback(
        async (siniestro: SiniestroItem) => {
            const visitaPresentada = siniestro.visitasRoam?.find(
                (v) => v.estadoId === VisitaEstado.PRESENTADO
            );
            if (!visitaPresentada) return;

            try {
                const response = await descargarExpedienteZip({
                    idVisita: Number(visitaPresentada.id)
                }).unwrap();

                const { presignedUrl, archivosAusentes } = response.data;

                if (archivosAusentes.length > 0) {
                    setZipPresignedUrl(presignedUrl);
                    setZipArchivosAusentes(archivosAusentes);
                    setZipDialogOpen(true);
                } else {
                    window.open(presignedUrl, '_blank');
                }
            } catch {
                enqueueSnackbar('Error al generar el expediente ZIP. Intente nuevamente.', {
                    variant: 'error'
                });
            }
        },
        [descargarExpedienteZip]
    );

    const handleConfirmarDescargaZip = useCallback(() => {
        if (zipPresignedUrl) {
            window.open(zipPresignedUrl, '_blank');
        }
        setZipDialogOpen(false);
        setZipPresignedUrl(null);
        setZipArchivosAusentes([]);
    }, [zipPresignedUrl]);

    const handleCancelarDescargaZip = useCallback(() => {
        setZipDialogOpen(false);
        setZipPresignedUrl(null);
        setZipArchivosAusentes([]);
    }, []);

    // Mismos filtros que la grilla, sin paginado: el back exporta todo lo filtrado
    const handleExportarExcel = useCallback(async () => {
        const { skip, take, ...filtrosExportacion } = queryParams;
        await exportToExcel(filtrosExportacion, 'Siniestros');
    }, [queryParams, exportToExcel]);

    return {
        activeTab,
        setActiveTab,
        siniestros,
        isLoading: isLoading || isFetching,
        totalCount: data?.count ?? 0,
        refetch,
        selectedIds,
        seleccionEstado,
        isSeleccionable,
        handleToggleSeleccion,
        handleSelectAll,
        handleClearSeleccion,
        handleGenerarTxtMasivo,
        isGenerandoTxt,
        handleDescargarZip,
        isLoadingZip,
        zipDialogOpen,
        zipArchivosAusentes,
        zipPresignedUrl,
        handleConfirmarDescargaZip,
        handleCancelarDescargaZip,
        handleExportarExcel,
        isExportandoExcel
    };
};

export default useSiniestros;
